/**
 * reportStyles.js
 *
 * Estilos compartidos para páginas de reportes de Prodenvases
 * Este archivo centraliza los estilos CSS para mantener consistencia
 * entre todas las páginas de reportes (DailyInventoryDistribution, ProviderInventoryDetail, etc.)
 *
 * Uso:
 * import { reportPageStyles, cardStyles, chartStyles, tableStyles, inputStyles } from '@/utils/reportStyles';
 */

// ============================================
// COLORES DEL SISTEMA (Consistente con backend)
// ============================================
export const STATUS_COLORS = {
  Negro: {
    hex: '#1f2937',
    rgb: '31, 41, 55',
    label: 'Negro',
    description: '≤10% de inventario',
    icon: '🔴',
    bg: 'bg-gray-800',
    text: 'text-gray-800',
    border: 'border-gray-800'
  },
  Rojo: {
    hex: '#dc2626',
    rgb: '220, 38, 38',
    label: 'Rojo',
    description: '10-30% de inventario',
    icon: '🟠',
    bg: 'bg-red-600',
    text: 'text-red-600',
    border: 'border-red-600'
  },
  Amarillo: {
    hex: '#ca8a04',
    rgb: '202, 138, 4',
    label: 'Amarillo',
    description: '30-60% de inventario',
    icon: '🟡',
    bg: 'bg-yellow-600',
    text: 'text-yellow-600',
    border: 'border-yellow-600'
  },
  Verde: {
    hex: '#16a34a',
    rgb: '22, 163, 74',
    label: 'Verde',
    description: '60-80% de inventario',
    icon: '🟢',
    bg: 'bg-green-600',
    text: 'text-green-600',
    border: 'border-green-600'
  },
  Azul: {
    hex: '#2563eb',
    rgb: '37, 99, 235',
    label: 'Azul',
    description: '>80% de inventario',
    icon: '🔵',
    bg: 'bg-blue-600',
    text: 'text-blue-600',
    border: 'border-blue-600'
  },
  SinClasificar: {
    hex: '#9ca3af',
    rgb: '156, 163, 175',
    label: 'N/A',
    description: 'Sin clasificar',
    icon: '⚪',
    bg: 'bg-gray-400',
    text: 'text-gray-400',
    border: 'border-gray-400'
  }
};

// ============================================
// PALETA DE COLORES PRINCIPAL
// ============================================
export const MAIN_COLORS = {
  primary: '#667eea',
  primaryDark: '#764ba2',
  secondary: '#146284',
  success: '#16a34a',
  warning: '#ca8a04',
  danger: '#dc2626',
  info: '#2563eb',

  // Grises
  gray50: '#f9fafb',
  gray100: '#f3f4f6',
  gray200: '#e5e7eb',
  gray300: '#d1d5db',
  gray400: '#9ca3af',
  gray500: '#6b7280',
  gray600: '#4b5563',
  gray700: '#374151',
  gray800: '#1f2937',
  gray900: '#111827',

  // Fondos
  background: '#f9fafb',
  surface: '#ffffff',

  // Bordes
  border: '#e5e7eb',
  borderLight: '#f3f4f6',
  borderDark: '#d1d5db'
};

// ============================================
// ESTILOS DE PÁGINA PRINCIPAL
// ============================================
export const reportPageStyles = {
  container: {
    padding: '24px',
    maxWidth: '100%',
    overflow: 'hidden',
    background: MAIN_COLORS.background,
    minHeight: '100vh'
  },
  containerMobile: {
    padding: '16px',
    maxWidth: '100%',
    overflow: 'hidden',
    background: MAIN_COLORS.background,
    minHeight: '100vh'
  },

  // Hero Header - El banner principal con gradiente
  heroHeader: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    borderRadius: '20px',
    padding: '32px 28px',
    color: 'white',
    position: 'relative',
    overflow: 'hidden',
    boxShadow: '0 10px 40px rgba(102, 126, 234, 0.2)'
  },
  heroHeaderMobile: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    borderRadius: '20px',
    padding: '24px 20px',
    color: 'white',
    position: 'relative',
    overflow: 'hidden',
    boxShadow: '0 10px 40px rgba(102, 126, 234, 0.2)'
  },

  // Decoración de fondo del header (círculos borrosos)
  heroDecoration1: {
    position: 'absolute',
    top: '-50%',
    right: '-10%',
    width: '300px',
    height: '300px',
    background: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '50%',
    filter: 'blur(40px)'
  },
  heroDecoration2: {
    position: 'absolute',
    bottom: '-30%',
    left: '-5%',
    width: '200px',
    height: '200px',
    background: 'rgba(255, 255, 255, 0.08)',
    borderRadius: '50%',
    filter: 'blur(30px)'
  },

  // Icono del header
  heroIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '56px',
    height: '56px',
    borderRadius: '14px',
    background: 'rgba(255, 255, 255, 0.2)',
    fontSize: '28px'
  },
  heroIconMobile: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '48px',
    height: '48px',
    borderRadius: '14px',
    background: 'rgba(255, 255, 255, 0.2)',
    fontSize: '24px'
  },

  // Título del header
  heroTitle: {
    color: 'white',
    marginBottom: '4px',
    fontWeight: 700,
    fontSize: '24px'
  },
  heroTitleMobile: {
    color: 'white',
    marginBottom: '4px',
    fontWeight: 700,
    fontSize: '18px'
  },

  // Descripción del header
  heroDescription: {
    color: 'rgba(255, 255, 255, 0.9)',
    fontSize: '14px',
    marginBottom: 0,
    display: 'block'
  },
  heroDescriptionMobile: {
    color: 'rgba(255, 255, 255, 0.9)',
    fontSize: '13px',
    marginBottom: 0,
    display: 'block'
  }
};

// ============================================
// ESTILOS DE TARJETAS (CARDS)
// ============================================
export const cardStyles = {
  default: {
    borderRadius: '16px',
    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
    border: `1px solid ${MAIN_COLORS.border}`,
    backgroundColor: MAIN_COLORS.surface
  },
  compact: {
    borderRadius: '12px',
    boxShadow: '0 1px 8px rgba(0, 0, 0, 0.04)',
    border: `1px solid ${MAIN_COLORS.borderLight}`,
    backgroundColor: MAIN_COLORS.surface
  },
  elevated: {
    borderRadius: '16px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
    border: `1px solid ${MAIN_COLORS.border}`,
    backgroundColor: MAIN_COLORS.surface
  },

  // Títulos de cards
  cardTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: MAIN_COLORS.gray900
  },
  cardTitleMobile: {
    fontSize: '16px',
    fontWeight: 600,
    color: MAIN_COLORS.gray900
  },

  // Contenido de cards
  cardBody: {
    padding: '20px'
  },
  cardBodyMobile: {
    padding: '16px'
  },

  // Card para filtros
  filterCard: {
    borderRadius: '16px',
    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
    border: `1px solid ${MAIN_COLORS.border}`,
    backgroundColor: MAIN_COLORS.surface,
    marginBottom: '16px'
  }
};

// ============================================
// ESTILOS DE GRÁFICOS (CHARTS)
// ============================================
export const chartStyles = {
  container: {
    borderRadius: '12px',
    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
    border: `1px solid ${MAIN_COLORS.border}`,
    backgroundColor: MAIN_COLORS.surface,
    padding: '16px'
  },

  // Tooltip personalizado
  tooltip: {
    backgroundColor: MAIN_COLORS.surface,
    border: `1px solid ${MAIN_COLORS.borderDark}`,
    borderRadius: '8px',
    padding: '12px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
  },
  tooltipTitle: {
    fontWeight: '600',
    color: MAIN_COLORS.gray900,
    marginBottom: '4px',
    fontSize: '14px'
  },
  tooltipContent: {
    fontSize: '14px',
    color: MAIN_COLORS.gray500
  },
  tooltipValue: {
    fontWeight: '600',
    color: MAIN_COLORS.gray900
  },

  // Leyenda
  legend: {
    fontSize: '12px',
    color: MAIN_COLORS.gray700
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px'
  },
  legendColor: {
    width: '12px',
    height: '12px',
    borderRadius: '2px'
  }
};

// ============================================
// ESTILOS DE TABLAS
// ============================================
export const tableStyles = {
  container: {
    borderRadius: '16px',
    overflow: 'hidden',
    border: `1px solid ${MAIN_COLORS.border}`,
    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)'
  },

  // Encabezado de tabla
  header: {
    backgroundColor: '#146284',
    color: 'white',
    fontWeight: '600',
    fontSize: '12px',
    textTransform: 'uppercase',
    padding: '12px 8px',
    textAlign: 'left'
  },
  headerCell: {
    padding: '12px 16px',
    fontWeight: 600,
    color: MAIN_COLORS.gray700,
    fontSize: '13px',
    borderBottom: `2px solid ${MAIN_COLORS.border}`,
    backgroundColor: MAIN_COLORS.gray50
  },

  // Filas de tabla
  row: {
    borderBottom: `1px solid ${MAIN_COLORS.borderLight}`,
    transition: 'background-color 0.2s ease'
  },
  rowEven: {
    backgroundColor: MAIN_COLORS.gray50
  },
  rowHover: {
    backgroundColor: MAIN_COLORS.gray100
  },
  cell: {
    padding: '12px 16px',
    fontSize: '13px',
    color: MAIN_COLORS.gray700
  },

  // Paginación
  pagination: {
    padding: '16px 24px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderTop: `1px solid ${MAIN_COLORS.border}`
  }
};

// ============================================
// ESTILOS DE INPUTS Y FILTROS
// ============================================
export const inputStyles = {
  container: {
    marginBottom: '16px'
  },

  // Label
  label: {
    display: 'block',
    marginBottom: '8px',
    fontSize: '14px',
    fontWeight: 500,
    color: MAIN_COLORS.gray700
  },
  labelRequired: {
    display: 'block',
    marginBottom: '8px',
    fontSize: '14px',
    fontWeight: 500,
    color: MAIN_COLORS.gray700
  },

  // Input
  input: {
    width: '100%',
    padding: '8px 12px',
    borderRadius: '6px',
    border: `1px solid ${MAIN_COLORS.border}`,
    fontSize: '14px',
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
    outline: 'none'
  },
  inputFocus: {
    borderColor: MAIN_COLORS.primary,
    boxShadow: `0 0 0 3px rgba(102, 126, 234, 0.1)`
  },

  // Select
  select: {
    width: '100%',
    padding: '8px 12px',
    borderRadius: '6px',
    border: `1px solid ${MAIN_COLORS.border}`,
    fontSize: '14px'
  },

  // DatePicker
  datePicker: {
    width: '100%',
    borderRadius: '6px'
  },

  // Checkbox
  checkbox: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer'
  },
  checkboxLabel: {
    fontSize: '14px',
    color: MAIN_COLORS.gray700
  },

  // Grupo de filtros
  filterGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  filterRow: {
    display: 'flex',
    gap: '16px',
    flexWrap: 'wrap'
  },
  filterCol: {
    flex: '1 1 200px'
  }
};

// ============================================
// ESTILOS DE BOTONES
// ============================================
export const buttonStyles = {
  primary: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    borderColor: 'transparent',
    color: 'white',
    borderRadius: '8px',
    fontWeight: 500,
    transition: 'all 0.2s ease'
  },
  secondary: {
    backgroundColor: MAIN_COLORS.surface,
    borderColor: MAIN_COLORS.border,
    color: MAIN_COLORS.gray700,
    borderRadius: '8px',
    fontWeight: 500
  },
  danger: {
    backgroundColor: MAIN_COLORS.danger,
    borderColor: 'transparent',
    color: 'white',
    borderRadius: '8px'
  },
  ghost: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    borderColor: 'rgba(255, 255, 255, 0.3)',
    color: 'white',
    borderRadius: '8px'
  },
  iconButton: {
    padding: '6px 12px',
    borderRadius: '6px',
    border: `1px solid ${MAIN_COLORS.borderDark}`,
    backgroundColor: MAIN_COLORS.surface,
    color: MAIN_COLORS.gray700,
    fontSize: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  }
};

// ============================================
// ESTILOS DE ALERTS Y MENSAJES
// ============================================
export const alertStyles = {
  info: {
    borderRadius: '12px',
    border: `1px solid #bfdbfe`,
    backgroundColor: '#eff6ff'
  },
  warning: {
    borderRadius: '12px',
    border: `1px solid #fef08a`,
    backgroundColor: '#fefce8'
  },
  success: {
    borderRadius: '12px',
    border: `1px solid #bbf7d0`,
    backgroundColor: '#f0fdf4'
  },
  error: {
    borderRadius: '12px',
    border: `2px solid ${MAIN_COLORS.danger}`,
    backgroundColor: '#fef2f2'
  }
};

// ============================================
// ESTILOS DE SEPARADORES
// ============================================
export const dividerStyles = {
  section: {
    fontSize: '18px',
    fontWeight: 600,
    color: MAIN_COLORS.gray900,
    margin: '28px 0'
  },
  sectionMobile: {
    fontSize: '16px',
    fontWeight: 600,
    color: MAIN_COLORS.gray900,
    margin: '20px 0'
  },
  subsection: {
    fontSize: '16px',
    fontWeight: 500,
    color: MAIN_COLORS.gray700,
    margin: '20px 0 12px 0'
  }
};

// ============================================
// ESTILOS DE LEYENDAS
// ============================================
export const legendStyles = {
  container: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
    alignItems: 'center',
    padding: '16px 0'
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  colorBox: {
    width: '14px',
    height: '14px',
    borderRadius: '4px'
  },
  label: {
    fontSize: '13px',
    fontWeight: 600,
    color: MAIN_COLORS.gray900
  },
  description: {
    fontSize: '12px',
    color: MAIN_COLORS.gray500
  }
};

// ============================================
// ESTILOS DE BADGES/TAGS
// ============================================
export const badgeStyles = {
  default: {
    fontSize: '12px',
    fontWeight: 600,
    padding: '4px 10px',
    borderRadius: '6px'
  },
  status: (color) => ({
    fontSize: '11px',
    fontWeight: 600,
    padding: '0 6px',
    height: '20px',
    lineHeight: '20px',
    backgroundColor: STATUS_COLORS[color]?.hex || STATUS_COLORS.SinClasificar.hex,
    color: 'white',
    borderRadius: '4px',
    display: 'inline-block'
  })
};

// ============================================
// ESTILOS DE LOADING Y ESTADOS VACÍOS
// ============================================
export const loadingStyles = {
  container: {
    padding: '24px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center'
  },
  card: {
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    borderRadius: '24px',
    padding: '48px 40px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    maxWidth: '400px',
    width: '90%',
    textAlign: 'center'
  }
};

export const emptyStateStyles = {
  container: {
    borderRadius: '16px',
    border: '2px dashed #d1d5db',
    backgroundColor: MAIN_COLORS.gray50,
    textAlign: 'center',
    padding: '48px'
  },
  icon: {
    fontSize: '64px',
    marginBottom: '16px'
  },
  title: {
    color: MAIN_COLORS.gray500,
    marginBottom: '12px',
    fontSize: '18px',
    fontWeight: 600
  },
  description: {
    fontSize: '16px',
    color: MAIN_COLORS.gray400
  }
};

// ============================================
// ESTILOS RESPONSIVE
// ============================================
export const responsive = {
  // Breakpoints
  xs: '480px',
  sm: '576px',
  md: '768px',
  lg: '992px',
  xl: '1200px',
  xxl: '1600px',

  // Helpers
  isMobile: (width) => width < 768,
  isTablet: (width) => width >= 768 && width < 992,
  isDesktop: (width) => width >= 992,

  // Spacing responsive
  padding: (isMobile) => isMobile ? '16px' : '24px',
  gap: (isMobile) => isMobile ? '12px' : '20px',
  fontSize: (isMobile, regular, mobile) => isMobile ? mobile : regular
};

// ============================================
// ANIMACIONES
// ============================================
export const animations = {
  fadeIn: {
    animation: 'fadeIn 0.3s ease-in-out'
  },
  slideIn: {
    animation: 'slideIn 0.3s ease-out'
  },
  pulse: {
    animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
  },
  spin: {
    animation: 'spin 1s linear infinite'
  }
};

// ============================================
// KEYFRAMES (para usar en styled-jsx o style tags)
// ============================================
export const keyframes = `
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @keyframes slideIn {
    from { transform: translateY(-10px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

export default {
  STATUS_COLORS,
  MAIN_COLORS,
  reportPageStyles,
  cardStyles,
  chartStyles,
  tableStyles,
  inputStyles,
  buttonStyles,
  alertStyles,
  dividerStyles,
  legendStyles,
  badgeStyles,
  loadingStyles,
  emptyStateStyles,
  responsive,
  animations,
  keyframes
};
